// B2B Lead Form — captures Company / Contact / Email / Phone / Volume / Interest / Message
// and POSTs to /api/b2b/leads. Emails admin + customer confirmation.
import { useState } from "react";
import { Building2, Mail, Phone, User as UserIcon, TrendingUp, CheckCircle2, ArrowRight, MessageSquare } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { api } from "../lib/api";

const VOLUMES = ["under_10", "10_25", "25_100", "100_plus"];
const INTERESTS = [
  "lieferung", "autoteile", "reinigung", "umzug",
  "escooter", "plakatservice", "gartenpflege", "other",
];

export default function B2BLeadForm() {
  const { t } = useLang();
  const l = t.b2b_lead || {};
  const [form, setForm] = useState({
    company: "", contact_name: "", email: "", phone: "",
    volume: "10_25", interest: "lieferung", message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.company.trim() || !form.contact_name.trim() || !form.email.trim()) {
      setError(l.required);
      return;
    }
    setSending(true);
    try {
      await api.post("/b2b/leads", form);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || l.error_generic);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="b2b-lead"
      className="bg-white border-y border-neutral-200 py-20 lg:py-28"
      data-testid="b2b-lead-section"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
        <div className="lg:col-span-2">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#FF5A00] mb-3 inline-flex items-center gap-2">
            <span className="w-6 h-px bg-[#FF5A00]" /> {l.eyebrow}
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.02] mb-5">
            {l.title}
          </h2>
          <p className="text-neutral-600 leading-relaxed mb-8">
            {l.subtitle}
          </p>
          <ul className="space-y-3">
            {[l.vol_25_100, "DATEV-Sammelrechnung", "Ansprechpartner + WhatsApp-Direktkanal"].map((it) => (
              <li key={it} className="flex items-start gap-2.5 text-sm text-neutral-700">
                <CheckCircle2 size={16} className="flex-shrink-0 text-[#FF5A00] mt-0.5" />
                {it}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-3">
          {done ? (
            <div
              className="bg-emerald-50 border border-emerald-200 p-8 flex flex-col items-start gap-3"
              data-testid="b2b-lead-success"
            >
              <CheckCircle2 size={32} className="text-emerald-600" />
              <h3 className="text-2xl font-black tracking-tight">{l.success_title}</h3>
              <p className="text-neutral-700 leading-relaxed">{l.success_body}</p>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="bg-neutral-50 border border-neutral-200 p-6 sm:p-8 space-y-5"
              data-testid="b2b-lead-form"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field icon={Building2} label={l.f_company} htmlFor="lead-company" required>
                  <input
                    id="lead-company"
                    type="text"
                    required
                    value={form.company}
                    onChange={upd("company")}
                    placeholder={l.f_company_ph}
                    className="w-full bg-white border border-neutral-300 focus:border-[#FF5A00] focus:outline-none px-3 py-2.5 text-sm transition-colors"
                    data-testid="b2b-lead-company"
                  />
                </Field>

                <Field icon={UserIcon} label={l.f_name} htmlFor="lead-name" required>
                  <input
                    id="lead-name"
                    type="text"
                    required
                    value={form.contact_name}
                    onChange={upd("contact_name")}
                    placeholder={l.f_name_ph}
                    className="w-full bg-white border border-neutral-300 focus:border-[#FF5A00] focus:outline-none px-3 py-2.5 text-sm transition-colors"
                    data-testid="b2b-lead-name"
                  />
                </Field>

                <Field icon={Mail} label={l.f_email} htmlFor="lead-email" required>
                  <input
                    id="lead-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={upd("email")}
                    placeholder={l.f_email_ph}
                    className="w-full bg-white border border-neutral-300 focus:border-[#FF5A00] focus:outline-none px-3 py-2.5 text-sm transition-colors"
                    data-testid="b2b-lead-email"
                  />
                </Field>

                <Field icon={Phone} label={l.f_phone} htmlFor="lead-phone">
                  <input
                    id="lead-phone"
                    type="tel"
                    value={form.phone}
                    onChange={upd("phone")}
                    placeholder={l.f_phone_ph}
                    className="w-full bg-white border border-neutral-300 focus:border-[#FF5A00] focus:outline-none px-3 py-2.5 text-sm transition-colors"
                    data-testid="b2b-lead-phone"
                  />
                </Field>
              </div>

              <Field icon={TrendingUp} label={l.f_volume} htmlFor="lead-volume" required help={l.f_volume_help}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {VOLUMES.map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setForm({ ...form, volume: v })}
                      className={`px-3 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors border ${
                        form.volume === v
                          ? "bg-[#0A0B0D] text-white border-[#0A0B0D]"
                          : "bg-white border-neutral-300 hover:border-[#FF5A00]"
                      }`}
                      data-testid={`b2b-lead-volume-${v}`}
                    >
                      {l[`vol_${v}`]}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label={l.f_interest} htmlFor="lead-interest">
                <select
                  id="lead-interest"
                  value={form.interest}
                  onChange={upd("interest")}
                  className="w-full bg-white border border-neutral-300 focus:border-[#FF5A00] focus:outline-none px-3 py-2.5 text-sm transition-colors"
                  data-testid="b2b-lead-interest"
                >
                  {INTERESTS.map((k) => (
                    <option key={k} value={k}>{l[`int_${k}`]}</option>
                  ))}
                </select>
              </Field>

              <Field icon={MessageSquare} label={l.f_message} htmlFor="lead-msg">
                <textarea
                  id="lead-msg"
                  rows={3}
                  value={form.message}
                  onChange={upd("message")}
                  placeholder={l.f_message_ph}
                  className="w-full bg-white border border-neutral-300 focus:border-[#FF5A00] focus:outline-none px-3 py-2.5 text-sm transition-colors resize-none"
                  data-testid="b2b-lead-message"
                />
              </Field>

              {error && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="b2b-lead-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={sending}
                className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E04F00] disabled:bg-neutral-400 text-white font-bold uppercase tracking-wide px-7 py-4 transition-colors"
                data-testid="b2b-lead-submit"
              >
                {sending ? l.sending : l.submit}
                {!sending && <ArrowRight size={16} />}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ icon: Icon, label, htmlFor, required, help, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="text-[11px] font-bold uppercase tracking-widest text-neutral-600 mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon size={12} className="text-[#FF5A00]" />}
        {label}
        {required && <span className="text-[#FF5A00]">*</span>}
      </label>
      {children}
      {help && <p className="text-[11px] text-neutral-500 mt-1">{help}</p>}
    </div>
  );
}
